import { normalizeAccountCurrency } from "./accountCurrency.ts";
import { formatCreditsAsMoney } from "./billingPrices.ts";

// Read-only Speak account snapshot for the in-app Symphony widget.
// Queries run with the caller's Supabase client so RLS applies.
// This module must stay free of Deno-only and DOM-only APIs.

export const ACCOUNT_CONTEXT_CORS: Record<string, string> = {
  "Access-Control-Allow-Origin": "*",
  "Access-Control-Allow-Headers": "authorization, x-client-info, apikey, content-type",
  "Access-Control-Allow-Methods": "GET, POST, OPTIONS",
  "Cache-Control": "no-store",
};

export interface SymphonyAccountContext {
  readOnly: true;
  user: {
    id: string;
    email: string | null;
    fullName: string | null;
    companyName: string | null;
  };
  billing: {
    readOnly: true;
    tier: string | null;
    status: string | null;
    subscribed: boolean | null;
    balance: string | null;
    rolloverBalance: string | null;
    subscriptionEnd: string | null;
    currency: string | null;
    billingExempt: boolean | null;
    baseConcurrentCalls: number | null;
    extraConcurrentCalls: number | null;
  };
  agents: Array<{
    id: string;
    name: string;
    industry: string | null;
    useCase: string | null;
    language: string | null;
    grade: string | null;
    isActive: boolean;
    phoneNumberId: string | null;
  }>;
  numbers: Array<{
    id: string;
    phoneNumber: string;
    friendlyName: string | null;
    countryCode: string | null;
    status: string | null;
    isDefault: boolean;
    provider: string | null;
  }>;
  demoPages: Array<{
    id: string;
    customerName: string;
    slug: string;
    embedType: string;
    isActive: boolean;
    symphonyAgentId: string | null;
  }>;
}

interface QueryError {
  message: string;
}

interface QueryResult<T> {
  data: T | null;
  error: QueryError | null;
}

interface RowFilter {
  eq: (column: string, value: string) => RowFilter;
  order: (column: string, options: { ascending: boolean }) => PromiseLike<QueryResult<unknown[]>>;
  maybeSingle: () => PromiseLike<QueryResult<Record<string, unknown>>>;
}

export interface SymphonyReadClient {
  from: (table: string) => {
    select: (columns: string) => RowFilter;
  };
}

const AGENT_COLUMNS = "id,name,industry,use_case,language,grade,is_active,phone_number_id";
const NUMBER_COLUMNS = "id,phone_number,friendly_name,country_code,status,is_default,provider";
const DEMO_COLUMNS = "id,customer_name,slug,embed_type,is_active,symphony_agent_id,created_by";
const BILLING_COLUMNS = "subscription_tier,subscription_status,subscribed,credits,credits_rollover,subscription_end,billing_currency,billing_exempt,base_concurrent_calls,extra_concurrent_calls";
const PROFILE_COLUMNS = "id,email,full_name,company_name";

export const ACCOUNT_CONTEXT_SELECTS = {
  agents: AGENT_COLUMNS,
  numbers: NUMBER_COLUMNS,
  demoPages: DEMO_COLUMNS,
  billing: BILLING_COLUMNS,
  profile: PROFILE_COLUMNS,
} as const;

function asRows(result: QueryResult<unknown[]>, label: string): Record<string, unknown>[] {
  if (result.error) throw new Error(`${label}: ${result.error.message}`);
  return Array.isArray(result.data) ? result.data as Record<string, unknown>[] : [];
}

function asRow(result: QueryResult<Record<string, unknown>>, label: string): Record<string, unknown> | null {
  if (result.error) throw new Error(`${label}: ${result.error.message}`);
  return result.data;
}

function str(row: Record<string, unknown>, key: string): string | null {
  const value = row[key];
  return typeof value === "string" ? value : null;
}

function bool(row: Record<string, unknown>, key: string): boolean | null {
  const value = row[key];
  return typeof value === "boolean" ? value : null;
}

function num(row: Record<string, unknown>, key: string): number | null {
  const value = row[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export async function loadSymphonyAccountContext(
  supabase: SymphonyReadClient,
  userId: string,
): Promise<SymphonyAccountContext> {
  const agentsQuery = supabase.from("agents").select(AGENT_COLUMNS).eq("user_id", userId);
  const numbersQuery = supabase.from("phone_numbers").select(NUMBER_COLUMNS).eq("user_id", userId);
  const demosQuery = supabase.from("demo_pages").select(DEMO_COLUMNS).eq("created_by", userId);
  const billingQuery = supabase.from("subscribers").select(BILLING_COLUMNS).eq("user_id", userId);
  const profileQuery = supabase.from("profiles").select(PROFILE_COLUMNS).eq("id", userId);

  const [agents, numbers, demos, billing, profile] = await Promise.all([
    agentsQuery.order("created_at", { ascending: false }),
    numbersQuery.order("created_at", { ascending: false }),
    demosQuery.order("created_at", { ascending: false }),
    billingQuery.maybeSingle(),
    profileQuery.maybeSingle(),
  ]);

  const agentRows = asRows(agents, "agents");
  const numberRows = asRows(numbers, "numbers");
  const demoRows = asRows(demos, "demo_pages");
  const billingRow = asRow(billing, "subscribers");
  const profileRow = asRow(profile, "profiles");

  return {
    readOnly: true,
    user: {
      id: userId,
      email: profileRow ? str(profileRow, "email") : null,
      fullName: profileRow ? str(profileRow, "full_name") : null,
      companyName: profileRow ? str(profileRow, "company_name") : null,
    },
    billing: {
      readOnly: true,
      tier: billingRow ? str(billingRow, "subscription_tier") : null,
      status: billingRow ? str(billingRow, "subscription_status") : null,
      subscribed: billingRow ? bool(billingRow, "subscribed") : null,
      balance: billingRow
        ? formatCreditsAsMoney(num(billingRow, "credits") ?? 0, normalizeAccountCurrency(str(billingRow, "billing_currency")))
        : null,
      rolloverBalance: billingRow
        ? formatCreditsAsMoney(num(billingRow, "credits_rollover") ?? 0, normalizeAccountCurrency(str(billingRow, "billing_currency")))
        : null,
      subscriptionEnd: billingRow ? str(billingRow, "subscription_end") : null,
      currency: billingRow ? str(billingRow, "billing_currency") : null,
      billingExempt: billingRow ? bool(billingRow, "billing_exempt") : null,
      baseConcurrentCalls: billingRow ? num(billingRow, "base_concurrent_calls") : null,
      extraConcurrentCalls: billingRow ? num(billingRow, "extra_concurrent_calls") : null,
    },
    agents: agentRows.map((row) => ({
      id: str(row, "id") ?? "",
      name: str(row, "name") ?? "",
      industry: str(row, "industry"),
      useCase: str(row, "use_case"),
      language: str(row, "language"),
      grade: str(row, "grade"),
      isActive: bool(row, "is_active") ?? false,
      phoneNumberId: str(row, "phone_number_id"),
    })),
    numbers: numberRows.map((row) => ({
      id: str(row, "id") ?? "",
      phoneNumber: str(row, "phone_number") ?? "",
      friendlyName: str(row, "friendly_name"),
      countryCode: str(row, "country_code"),
      status: str(row, "status"),
      isDefault: bool(row, "is_default") ?? false,
      provider: str(row, "provider"),
    })),
    demoPages: demoRows.map((row) => ({
      id: str(row, "id") ?? "",
      customerName: str(row, "customer_name") ?? "",
      slug: str(row, "slug") ?? "",
      embedType: str(row, "embed_type") ?? "elevenlabs",
      isActive: bool(row, "is_active") ?? false,
      symphonyAgentId: str(row, "symphony_agent_id"),
    })),
  };
}

export interface AuthenticatedAccountContext {
  userId: string;
  load: () => Promise<SymphonyAccountContext>;
}

export interface AccountContextDeps {
  authenticate: (token: string) => Promise<AuthenticatedAccountContext | null>;
}

function json(status: number, body: unknown): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { ...ACCOUNT_CONTEXT_CORS, "Content-Type": "application/json" },
  });
}

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    const pad = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
    const parsed = JSON.parse(globalThis.atob(padded + pad));
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function bearerToken(req: Request): string | null {
  const header = req.headers.get("Authorization") ?? req.headers.get("authorization");
  if (!header) return null;
  const match = header.match(/^Bearer\s+(\S+)$/i);
  return match?.[1] ?? null;
}

/**
 * HTTP handler for the account-context endpoint.
 * Reads the caller's JWT, requires role=authenticated, then loads via that same JWT.
 * Never writes. Never uses the service role.
 */
export async function handleAccountContextRequest(req: Request, deps: AccountContextDeps): Promise<Response> {
  if (req.method === "OPTIONS") {
    return new Response(null, { status: 204, headers: ACCOUNT_CONTEXT_CORS });
  }
  if (req.method !== "GET" && req.method !== "POST") {
    return json(405, { error: "method_not_allowed" });
  }

  const token = bearerToken(req);
  if (!token) return json(401, { error: "missing_token" });

  const payload = decodeJwtPayload(token);
  if (!payload || payload.role !== "authenticated") {
    return json(403, { error: "user_token_required" });
  }

  const authed = await deps.authenticate(token);
  if (!authed) return json(401, { error: "invalid_token" });

  try {
    const context = await authed.load();
    return json(200, context);
  } catch (error) {
    console.error("[symphony-account-context]", error instanceof Error ? error.message : "failed");
    return json(500, { error: "account_context_failed" });
  }
}
